import { Dispatch, MiddlewareAPI } from 'redux'
import { toast } from 'react-toastify'

import {
  GET_PRODUCT_ALIASES,
  GET_PRODUCT_ALIASES_SUCCESS,
  GET_PRODUCT_ALIASES_ERROR,
  POST_PRODUCT_ALIASES,
  POST_PRODUCT_ALIASES_SUCCESS,
  POST_PRODUCT_ALIASES_ERROR,
  setProductAliases,
  setBatchProductAliases,
  updateProductAliases,
  setBatchAliasAssignmentsIsConfirm,
} from '../actions/productAliases'
import { ProductAliasesActionTypes } from '../types/productAliases'
import { API_ALIASES, API_PRODUCTS } from '../../consts'
import { apiRequest } from '../actions/api'
import { setBatchLookupTimeouts, setBatchProgressError } from '../actions/ui'
import { handleGenericErrors } from '../../utils/Products/errorHandler'

const getProductAliasesMiddleware =
  ({ dispatch }: { dispatch: Dispatch }) =>
  (next: Dispatch) =>
  async (action: ProductAliasesActionTypes) => {
    const result = next(action)

    if (action.type === GET_PRODUCT_ALIASES) {
      const productIds: string[] = action.payload.productIds

      dispatch(
        apiRequest({
          url: API_ALIASES,
          method: 'post',
          data: {
            ids: productIds,
          },
          entity: 'productAliases',
          withCredentials: true,
          onSuccess: GET_PRODUCT_ALIASES_SUCCESS,
          onError: GET_PRODUCT_ALIASES_ERROR,
          originalPayload: action.payload,
        })
      )
    }

    return result
  }

const getProductAliasesSuccessMiddleware =
  (store: MiddlewareAPI) => (next: Dispatch) => async (action: ProductAliasesActionTypes) => {
    const result = next(action)
    const dispatch = store.dispatch

    if (action.type === GET_PRODUCT_ALIASES_SUCCESS) {
      if (action.meta.originalPayload.batchId) {
        const batchId = action.meta.originalPayload.batchId
        dispatch(
          setBatchProductAliases({
            aliases: action.payload.data.data,
            batchId: batchId,
          })
        )
      } else {
        dispatch(setProductAliases({ aliases: action.payload.data.data }))
      }
    }

    return result
  }

const getProductAliasesErrorMiddleware =
  ({ dispatch }: { dispatch: Dispatch }) =>
  (next: Dispatch) =>
  async (action: ProductAliasesActionTypes) => {
    const result = next(action)

    if (action.type === GET_PRODUCT_ALIASES_ERROR) {
      if (action.meta.originalPayload.batchId) {
        dispatch(setBatchProgressError({ batchId: action.meta.originalPayload.batchId }))
        dispatch(setBatchLookupTimeouts({ timeouts: [] }))
      }

      if (!toast.isActive('get-product-aliases-error')) {
        toast('Get product aliases error', {
          containerId: 'error',
          toastId: 'get-product-aliases-error',
        })
      }

      new Error('getProductAliases request error')
    }

    return result
  }

const postProductAliasesMiddleware =
  ({ dispatch }: { dispatch: Dispatch }) =>
  (next: Dispatch) =>
  async (action: ProductAliasesActionTypes) => {
    const result = next(action)

    if (action.type === POST_PRODUCT_ALIASES) {
      const data: Record<string, string> = {
        alias: action.payload.alias,
      }
      if (action.payload.requestUuid) {
        data.request_uuid = action.payload.requestUuid
      }
      dispatch(
        apiRequest({
          method: 'POST',
          url: `${API_PRODUCTS}/${action.payload.id}/aliases`,
          entity: 'productAliases',
          headers: { 'content-type': 'application/json' },
          withCredentials: true,
          data: data,
          originalPayload: action.payload,
          onSuccess: POST_PRODUCT_ALIASES_SUCCESS,
          onError: POST_PRODUCT_ALIASES_ERROR,
        })
      )
    }

    return result
  }

const postProductAliasesSuccessMiddleware =
  ({ dispatch }: { dispatch: Dispatch }) =>
  (next: Dispatch) =>
  async (action: ProductAliasesActionTypes) => {
    const result = next(action)

    if (action.type === POST_PRODUCT_ALIASES_SUCCESS) {
      if (action.meta.originalPayload.batchId) {
        dispatch(
          setBatchAliasAssignmentsIsConfirm({
            batchId: action.meta.originalPayload.batchId,
            isConfirmed: true,
          })
        )
      } else {
        dispatch(updateProductAliases(action.payload.data))
      }
      toast('Alias saved', { containerId: 'default' })
    }

    return result
  }

const postProductAliasesErrorMiddleware =
  () => (next: Dispatch) => async (action: ProductAliasesActionTypes) => {
    const result = next(action)

    if (action.type === POST_PRODUCT_ALIASES_ERROR) {
      const responseError = action?.payload?.response?.data.errors[0]
      handleGenericErrors(action.payload, responseError, { autoClose: 10000 })
    }

    return result
  }

export const productAliasesMiddlewares = [
  getProductAliasesMiddleware,
  getProductAliasesSuccessMiddleware,
  getProductAliasesErrorMiddleware,
  postProductAliasesMiddleware,
  postProductAliasesSuccessMiddleware,
  postProductAliasesErrorMiddleware,
]
